<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { computed } from 'vue';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { useTranslations } from '@/composables/useTranslations';
import { localizeHref } from '@/lib/localizeHref';
import type { BreadcrumbItem as BreadcrumbItemType } from '@/types';

type Props = {
    breadcrumbs: BreadcrumbItemType[];
};

const props = defineProps<Props>();

const { t, locale } = useTranslations();

const localizedBreadcrumbs = computed(() =>
    props.breadcrumbs.map((item) => ({
        ...item,
        href: localizeHref(item.href, locale.value),
    })),
);

function crumbLabel(item: BreadcrumbItemType): string {
    return item.translationKey
        ? t(item.translationKey, item.title)
        : item.title;
}
</script>

<template>
    <Breadcrumb>
        <BreadcrumbList>
            <template
                v-for="(item, index) in localizedBreadcrumbs"
                :key="index"
            >
                <BreadcrumbItem>
                    <template v-if="index === localizedBreadcrumbs.length - 1">
                        <BreadcrumbPage>{{ crumbLabel(item) }}</BreadcrumbPage>
                    </template>
                    <template v-else>
                        <BreadcrumbLink as-child>
                            <Link :href="item.href">{{
                                crumbLabel(item)
                            }}</Link>
                        </BreadcrumbLink>
                    </template>
                </BreadcrumbItem>
                <BreadcrumbSeparator
                    v-if="index !== localizedBreadcrumbs.length - 1"
                />
            </template>
        </BreadcrumbList>
    </Breadcrumb>
</template>
